// ─── Trust & Transparency Sprint — Trust Overview Modal ───────────────────────
//
// Triggered by "🛡 Why can I trust WorkRoamer?" link beneath the terms checkbox.
// A premium slide-over / modal that explains — in plain English — how WorkRoamer
// protects operators. NOT the full Terms of Service. Not legal language.
// Goal: operators leave thinking "I understand how my data is protected."

import { useEffect, useRef } from 'react';
import { useLocation } from 'wouter';
import { X, Shield, Lock, Cpu, ShieldCheck, UserCheck, Globe, Handshake, ChevronRight } from 'lucide-react';

// ─── Section data ─────────────────────────────────────────────────────────────

const SECTIONS = [
  {
    icon: Shield,
    emoji: '🛡',
    title: 'Your Security Comes First',
    color: '#3b82f6',
    colorBg: 'rgba(59,130,246,0.08)',
    colorBorder: 'rgba(59,130,246,0.2)',
    body: 'WorkRoamer is designed to protect your information using modern security practices and a privacy-first architecture. We believe trust is earned, not assumed — which is why security is built into every layer of the platform, not added as an afterthought.',
  },
  {
    icon: Lock,
    emoji: '🔒',
    title: 'Your Data Belongs To You',
    color: 'rgba(34,197,94,0.9)',
    colorBg: 'rgba(34,197,94,0.07)',
    colorBorder: 'rgba(34,197,94,0.22)',
    body: 'Everything you save in WorkRoamer — your documents, notes, research, learning history, and personal knowledge — belongs to you. We never sell your data, never share it with advertisers, and never use it to build profiles for external purposes. You can export or delete your information at any time.',
  },
  {
    icon: Cpu,
    emoji: '🤖',
    title: 'Responsible AI',
    color: 'rgba(139,92,246,0.9)',
    colorBg: 'rgba(139,92,246,0.07)',
    colorBorder: 'rgba(139,92,246,0.22)',
    body: 'AI helps organise and understand your information. We use it responsibly:',
    bullets: [
      'AI processing happens once and results are securely cached — we never repeat unnecessary calls.',
      'We minimise the information sent to AI providers, stripping credentials and sensitive patterns before every request.',
      'We never perform unnecessary AI requests or use AI simply because we can.',
      'Your content is never used to train AI models.',
    ],
    footer: 'Our goal is intelligent assistance while respecting your privacy.',
  },
  {
    icon: ShieldCheck,
    emoji: '🔐',
    title: 'Secure By Design',
    color: 'rgba(251,191,36,0.9)',
    colorBg: 'rgba(251,191,36,0.07)',
    colorBorder: 'rgba(251,191,36,0.22)',
    body: 'WorkRoamer is built around security-first engineering principles:',
    bullets: [
      'Secure authentication — your password is never stored, only a cryptographic hash.',
      'Encrypted communication — all data travels over HTTPS.',
      'Permission-based access — every request verifies ownership before responding.',
      'File validation — uploads are checked by file signature, not just filename.',
      'Secure cloud storage — data is stored in encrypted, managed infrastructure.',
      'Continuous security improvements — our engineering standards evolve with the platform.',
    ],
  },
  {
    icon: UserCheck,
    emoji: '👤',
    title: 'You Stay In Control',
    color: 'rgba(34,197,94,0.9)',
    colorBg: 'rgba(34,197,94,0.07)',
    colorBorder: 'rgba(34,197,94,0.22)',
    body: 'Your information should never feel trapped inside WorkRoamer. Operators can always:',
    bullets: [
      'Download all their data at any time.',
      'Export Vault knowledge and notes.',
      'Delete individual documents or memories.',
      'Disconnect integrations and connected accounts.',
      'Delete their account completely, with no questions asked.',
    ],
  },
  {
    icon: Globe,
    emoji: '🌍',
    title: 'Built For Digital Nomads',
    color: '#3b82f6',
    colorBg: 'rgba(59,130,246,0.08)',
    colorBorder: 'rgba(59,130,246,0.2)',
    body: 'WorkRoamer understands that remote workers often store important travel, work, and career information — from visa documents and income records to personal knowledge and career history. Our goal is to provide an operating system you can confidently rely on wherever you roam.',
  },
];

const PLEDGE = {
  emoji: '🤝',
  title: 'Our Commitment',
  body: 'At WorkRoamer, we believe trust is our most valuable feature. We are committed to building a platform that is secure, transparent, respectful of your privacy, and worthy of storing the information that powers your remote-working life.',
};

// ─── Component ────────────────────────────────────────────────────────────────

interface TrustOverviewModalProps {
  onClose: () => void;
}

export default function TrustOverviewModal({ onClose }: TrustOverviewModalProps) {
  const [, setLocation] = useLocation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Lock body scroll while open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const navigate = (path: string) => { onClose(); setLocation(path); };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Why can I trust WorkRoamer?"
      style={{
        position: 'fixed', inset: 0, zIndex: 10000,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        background: 'rgba(4,4,12,0.85)', backdropFilter: 'blur(10px)',
      }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* Sheet */}
      <div style={{
        width: '100%', maxWidth: 540,
        maxHeight: '92vh',
        background: '#0a0a1a',
        border: '1px solid rgba(59,130,246,0.25)',
        borderBottom: 'none',
        borderRadius: '20px 20px 0 0',
        display: 'flex', flexDirection: 'column',
        boxShadow: '0 -24px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(59,130,246,0.1)',
        animation: 'trustSlideUp 0.28s cubic-bezier(0.34,1.1,0.64,1)',
      }}>
        <style>{`
          @keyframes trustSlideUp {
            from { transform: translateY(60px); opacity: 0; }
            to   { transform: translateY(0);    opacity: 1; }
          }
        `}</style>

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '20px 22px 16px',
          borderBottom: '1px solid rgba(255,255,255,0.07)',
          flexShrink: 0,
        }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'rgba(59,130,246,0.12)',
            border: '1px solid rgba(59,130,246,0.25)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}>
            <Shield size={18} color="#3b82f6" />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: 'rgba(255,255,255,0.92)', letterSpacing: '-0.01em' }}>
              Why can I trust WorkRoamer?
            </div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.38)', marginTop: 1 }}>
              How we protect your information
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)',
              borderRadius: 8, width: 30, height: 30, cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'rgba(255,255,255,0.5)', flexShrink: 0,
            }}
          >
            <X size={14} />
          </button>
        </div>

        {/* Scrollable body */}
        <div ref={scrollRef} style={{ flex: 1, overflowY: 'auto', padding: '20px 22px' }}>

          {SECTIONS.map(({ icon: Icon, emoji, title, color, colorBg, colorBorder, body, bullets, footer }) => (
            <div key={title} style={{
              background: colorBg,
              border: `1px solid ${colorBorder}`,
              borderRadius: 12, padding: '16px 18px', marginBottom: 12,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 9, marginBottom: 10 }}>
                <span style={{ fontSize: 16, lineHeight: 1, flexShrink: 0 }}>{emoji}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>{title}</span>
              </div>
              <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.65, margin: bullets ? '0 0 10px' : 0 }}>
                {body}
              </p>
              {bullets && (
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {bullets.map((b, i) => (
                    <li key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                      <span style={{ color, flexShrink: 0, fontSize: 13, lineHeight: 1.5 }}>✓</span>
                      <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.7)', lineHeight: 1.55 }}>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
              {footer && (
                <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', lineHeight: 1.55, margin: '10px 0 0', fontStyle: 'italic' }}>
                  {footer}
                </p>
              )}
            </div>
          ))}

          {/* Pledge */}
          <div style={{
            background: 'linear-gradient(135deg, rgba(37,99,235,0.12) 0%, rgba(139,92,246,0.08) 100%)',
            border: '1px solid rgba(59,130,246,0.28)',
            borderRadius: 12, padding: '18px 20px', marginBottom: 4,
            textAlign: 'center',
          }}>
            <div style={{ fontSize: 22, marginBottom: 10 }}>🤝</div>
            <div style={{ fontSize: 13, fontWeight: 800, color: 'rgba(255,255,255,0.9)', marginBottom: 10 }}>
              Our Commitment
            </div>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.65)', lineHeight: 1.7, margin: 0 }}>
              {PLEDGE.body}
            </p>
          </div>
        </div>

        {/* Footer buttons */}
        <div style={{
          padding: '16px 22px 22px',
          borderTop: '1px solid rgba(255,255,255,0.07)',
          flexShrink: 0,
          display: 'flex', flexDirection: 'column', gap: 8,
        }}>
          {/* Primary */}
          <button
            onClick={onClose}
            style={{
              width: '100%',
              background: 'linear-gradient(90deg, #1a3fcc 0%, #2563eb 50%, #3b82f6 100%)',
              border: 'none', borderRadius: 10, color: '#fff',
              fontSize: 13, fontWeight: 800, letterSpacing: '0.1em',
              textTransform: 'uppercase', padding: '14px 0', cursor: 'pointer',
              boxShadow: '0 4px 18px rgba(37,99,235,0.4)',
            }}
          >
            I Understand
          </button>

          {/* Secondary row */}
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => navigate('/terms')}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: 'rgba(255,255,255,0.7)',
                fontSize: 11, fontWeight: 600, padding: '10px 0', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              Full Terms <ChevronRight size={11} />
            </button>
            <button
              onClick={() => navigate('/privacy')}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: 'rgba(255,255,255,0.7)',
                fontSize: 11, fontWeight: 600, padding: '10px 0', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              Privacy Policy <ChevronRight size={11} />
            </button>
            <button
              onClick={() => navigate('/security-trust')}
              style={{
                flex: 1, background: 'rgba(255,255,255,0.05)',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8, color: 'rgba(255,255,255,0.7)',
                fontSize: 11, fontWeight: 600, padding: '10px 0', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
              }}
            >
              Security <ChevronRight size={11} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
